// System Suitability Test specification per analysis definition (PRD-005 Area 3 / SPEC-017):
// whether an analysis definition REQUIRES an SST before its results can be sent to review, and the
// SST acceptance criteria (RSD-area ≤, RSD-RT ≤, tailing interval, plate count N ≥, resolution Rs ≥,
// and the expected injection count).
//
// SPEC-021 (B2): there is NO /lims/sstSpec endpoint. The SST spec is FOLDED onto the analysis-
// definition payload — it arrives as the nested `sstSpec` key on every definition dump and is
// WRITTEN via the definition save. This slice keeps the selector API (B-D3) and mirrors the
// definitions into the same map every consumer already reads. With showLims off this slice is
// inert (no SST UI mounts).

// state shape: { [analysisDefinitionId]: { requiresSst, rsdAreaMax, rsdRtMax, tailingMin,
//   tailingMax, plateCountMin, resolutionMin, injectionCount, instrumentType, defaultSstTemplateId } }
// F4 (SPEC-036): instrumentType (HPLC/GC) drives the SST template picker; defaultSstTemplateId is
// the optional shared template the run pre-selects. Both are folded onto the definition payload.
const initialState = {}

// Default expected injection count when none is configured. The typical HPLC SST is six replicate
// injections (guide ch. 4); used purely to pre-fill the panel's injection rows.
export const SST_DEFAULT_INJECTION_COUNT = 6

// Normalise a criterion bound to a finite number, or null when unset (→ that criterion is not
// judged). Kept tolerant of Danish comma input (the config Number control yields numbers, but a
// stored value may round-trip as a string).
function normalizeNumber(v) {
  if (v === "" || v === null || v === undefined) return null
  const n = Number(typeof v === "string" ? v.replace(",", ".") : v)
  return Number.isFinite(n) ? n : null
}

// A positive integer injection count, or the default.
function normalizeInjectionCount(v) {
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : SST_DEFAULT_INJECTION_COUNT
}

function normalizeSpec(item) {
  return {
    requiresSst: !!(item && item.requiresSst),
    rsdAreaMax: normalizeNumber(item && item.rsdAreaMax),
    rsdRtMax: normalizeNumber(item && item.rsdRtMax),
    tailingMin: normalizeNumber(item && item.tailingMin),
    tailingMax: normalizeNumber(item && item.tailingMax),
    plateCountMin: normalizeNumber(item && item.plateCountMin),
    resolutionMin: normalizeNumber(item && item.resolutionMin),
    injectionCount: normalizeInjectionCount(item && item.injectionCount),
    // F4 (SPEC-036): instrument type drives the template picker; default template pre-selects one.
    // Kept as-is (string / id or null); empty string → null so a blank select reads as "none".
    instrumentType: (item && item.instrumentType) || null,
    defaultSstTemplateId: (item && item.defaultSstTemplateId) || null,
  }
}

// Whether a definition dump carries a usable nested SST spec (null/absent = no row).
function sstSpecOf(definition) {
  return definition && definition.sstSpec && typeof definition.sstSpec === "object"
    ? definition.sstSpec
    : null
}

// Mirror one definition's nested SST spec into the map (or drop the entry when it has none).
function applyDefinition(map, definition) {
  if (!definition || !definition.id) return map
  const spec = sstSpecOf(definition)
  const next = { ...map }
  if (spec) {
    next[definition.id] = normalizeSpec(spec)
  } else if (next[definition.id]) {
    delete next[definition.id]
  }
  return next
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "analysisDefinitions/fetch": {
      const map = {}
      ;(action.res || []).forEach((item) => {
        const spec = sstSpecOf(item)
        if (spec && item.id) {
          map[item.id] = normalizeSpec(spec)
        }
      })
      return map
    }
    case "analysisDefinitions/create":
    case "analysisDefinitions/edit":
      return applyDefinition(state, action.res)
    case "analysisDefinitions/remove": {
      if (!action.res || !state[action.res.id]) return state
      const next = { ...state }
      delete next[action.res.id]
      return next
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// The SST spec for one analysis definition (or undefined if none stored yet).
export const selectSstSpec = (state, analysisDefinitionId) => state.limsSstSpec[analysisDefinitionId]

// Whether an analysis definition REQUIRES an SST (drives whether the SST panel + send gate apply).
// False for a definition with no stored spec or one whose spec has the toggle off.
export const selectRequiresSst = (state, analysisDefinitionId) => {
  const spec = analysisDefinitionId ? state.limsSstSpec[analysisDefinitionId] : undefined
  return !!(spec && spec.requiresSst)
}
